import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Plus, Archive, Bell, User, Shield } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CreateProjectDialog } from '@/components/CreateProjectDialog';

export const AppSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [createProjectOpen, setCreateProjectOpen] = useState(false);

  const isHomeActive = location.pathname === '/';

  return (
    <aside data-onboard="home-sidebar-nav" className="h-screen flex flex-col items-center py-3.5" style={{ width: 56, background: '#FFFFFF', borderRight: '1px solid #E8E6E1' }}>
      {/* Logo — Dilli bars */}
      <button onClick={() => navigate('/')} className="mb-5 flex items-center justify-center" style={{ width: 32, height: 32 }}>
        <div className="flex items-stretch gap-[3px]" style={{ height: 18 }}>
          <span className="block rounded-sm" style={{ width: 3.5, background: '#1A1A1A' }} />
          <span className="block rounded-sm" style={{ width: 3.5, background: '#1A1A1A' }} />
          <span className="block rounded-sm" style={{ width: 3.5, background: '#1A1A1A', height: '60%', alignSelf: 'flex-end' }} />
        </div>
      </button>

      {/* Nav items */}
      <div className="flex flex-col items-center gap-1">
        {([
          { icon: Home, label: 'Home', path: '/', active: isHomeActive },
          { icon: Plus, label: 'New Project', path: '__new__', active: false },
          { icon: Archive, label: 'Archived', path: '/archived', active: location.pathname === '/archived' },
        ] as const).map((item) => (
          <div key={item.label} className="relative group">
            <button
              onClick={() => item.path === '__new__' ? setCreateProjectOpen(true) : navigate(item.path)}
              className={cn(
                "flex items-center justify-center transition-all duration-100",
                item.active ? 'text-[#1A1A1A]' : 'text-[#8A8A84] hover:text-[#52524E] hover:bg-[#FAF9F6]'
              )}
              style={{ width: 38, height: 38, borderRadius: 8, background: item.active ? '#F2F1ED' : undefined }}
            >
              <item.icon style={{ width: 20, height: 20 }} />
            </button>
            {/* Tooltip */}
            <div className="absolute left-full ml-2.5 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-100 z-50"
              style={{ background: '#1A1D23', color: 'white', fontSize: 11.5, fontWeight: 500, padding: '4px 10px', borderRadius: 6, whiteSpace: 'nowrap' }}>
              {item.label}
            </div>
          </div>
        ))}
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Bottom */}
      <div className="flex flex-col items-center gap-1">
        {([
          { icon: User, label: 'Profile', path: '/profile' },
          { icon: Bell, label: 'Notifications', path: '/notifications', dot: true },
          { icon: Shield, label: 'Privacy & Terms', path: '/privacy' },
        ] as const).map((item) => (
          <div key={item.label} className="relative group">
            <button
              onClick={() => {
                if (item.path === '/notifications' || item.path === '/profile') {
                  // No route yet — coming soon
                  return;
                }
                navigate(item.path);
              }}
              className={cn(
                "flex items-center justify-center transition-all duration-100",
                location.pathname === item.path ? 'text-[#1A1A1A]' : 'text-[#8A8A84] hover:text-[#52524E] hover:bg-[#FAF9F6]'
              )}
              style={{ width: 38, height: 38, borderRadius: 8, background: location.pathname === item.path ? '#F2F1ED' : undefined }}
            >
              <item.icon style={{ width: 20, height: 20 }} />
              {'dot' in item && item.dot && (
                <span className="absolute" style={{ top: 8, right: 8, width: 6, height: 6, borderRadius: '50%', background: '#D4940A' }} />
              )}
            </button>
            <div className="absolute left-full ml-2.5 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-100 z-50"
              style={{ background: '#1A1D23', color: 'white', fontSize: 11.5, fontWeight: 500, padding: '4px 10px', borderRadius: 6, whiteSpace: 'nowrap' }}>
              {item.label}
            </div>
          </div>
        ))}
      </div>

      <CreateProjectDialog open={createProjectOpen} onOpenChange={setCreateProjectOpen} />
    </aside>
  );
};
